import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Camera, CheckCircle2, CalendarDays, Truck, Palette, DollarSign, Hash, MessageCircle, User, Mail, Phone } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import { api } from "@/lib/api";

const EVENT_TYPES = ["Birthday", "Baby Shower", "Bridal Shower", "Wedding", "Holiday", "Corporate", "Thank You / Gift", "Other"];
const BUDGET_RANGES = ["Under $50", "$50 – $100", "$100 – $200", "$200 – $400", "$400+"];
const DELIVERY = ["Local Pickup (preferred)", "Delivery — please quote", "Either is fine"];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const initial = {
  name: "", email: "", phone: "",
  event_date: "", delivery_preference: DELIVERY[0],
  event_type: EVENT_TYPES[0],
  treats_wanted: "", quantity: "", theme_colors: "",
  budget_range: BUDGET_RANGES[1],
  notes: "",
  inspiration_photo: "", inspiration_photo_name: "",
};

export default function CustomOrders() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [payUrl, setPayUrl] = useState("");
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Please upload an image under 4MB");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    const dataUrl = await fileToDataUrl(f);
    setForm((prev) => ({ ...prev, inspiration_photo: dataUrl, inspiration_photo_name: f.name }));
    toast.success("Photo attached");
  };

  const clearFile = () => {
    setForm((p) => ({ ...p, inspiration_photo: "", inspiration_photo_name: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please include your name and email");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, origin_url: window.location.origin };
      const { data } = await api.post("/inquiries", payload);
      setPayUrl(data?.pay_url || "");
      setDone(true);
      toast.success("Inquiry sent! Check your email — I'll reply soon.");
      setForm(initial);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-custom-orders">
      <section className="section">
        <div className="mx-auto max-w-5xl text-center">
          <span className="chip"><Sparkles className="h-3 w-3" /> Custom Orders</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">Let's create something sweet</h1>
          <p className="mt-4 text-sakura-soft max-w-2xl mx-auto">Please submit custom order requests at least 1–2 weeks in advance when possible. Rush orders may be accepted depending on availability.</p>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-4xl">
          {done ? (
            <div className="card-soft p-10 md:p-14 text-center" data-testid="inquiry-success">
              <span className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><CheckCircle2 className="h-6 w-6" /></span>
              <h2 className="mt-5 font-serif text-3xl text-sakura-ink">Thank you!</h2>
              <p className="mt-3 text-sakura-soft max-w-md mx-auto">Your inquiry was sent. I'll review the details and reply to your email within 1–2 business days with a custom quote. Once we've confirmed everything, I'll send a secure payment link to lock in your date.</p>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <button onClick={() => { setDone(false); setPayUrl(""); }} className="btn-secondary" data-testid="inquiry-send-another">Send another inquiry</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="card-soft p-6 md:p-10" data-testid="inquiry-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Name" icon={User} required>
                  <input className="input-soft" value={form.name} onChange={set("name")} required placeholder="Your name" data-testid="inquiry-name" />
                </Field>
                <Field label="Email" icon={Mail} required>
                  <input className="input-soft" type="email" value={form.email} onChange={set("email")} required placeholder="you@example.com" data-testid="inquiry-email" />
                </Field>
                <Field label="Phone number" icon={Phone}>
                  <input className="input-soft" type="tel" value={form.phone} onChange={set("phone")} placeholder="(optional)" data-testid="inquiry-phone" />
                </Field>
                <Field label="Event date" icon={CalendarDays}>
                  <input className="input-soft" type="date" value={form.event_date} onChange={set("event_date")} data-testid="inquiry-date" />
                </Field>
                <Field label="Pickup / Delivery preference" icon={Truck}>
                  <select className="input-soft" value={form.delivery_preference} onChange={set("delivery_preference")} data-testid="inquiry-delivery">
                    {DELIVERY.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Type of event" icon={Sparkles}>
                  <select className="input-soft" value={form.event_type} onChange={set("event_type")} data-testid="inquiry-event-type">
                    {EVENT_TYPES.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Treats wanted" icon={Sparkles} className="md:col-span-2">
                  <input className="input-soft" value={form.treats_wanted} onChange={set("treats_wanted")} placeholder="e.g., Cake pops, pretzels, Oreos" data-testid="inquiry-treats" />
                </Field>
                <Field label="Quantity needed" icon={Hash}>
                  <input className="input-soft" value={form.quantity} onChange={set("quantity")} placeholder="e.g., 2 dozen + 12 pretzels" data-testid="inquiry-quantity" />
                </Field>
                <Field label="Theme / colors" icon={Palette}>
                  <input className="input-soft" value={form.theme_colors} onChange={set("theme_colors")} placeholder="e.g., blush pink & gold, unicorn" data-testid="inquiry-theme" />
                </Field>
                <Field label="Budget range" icon={DollarSign}>
                  <select className="input-soft" value={form.budget_range} onChange={set("budget_range")} data-testid="inquiry-budget">
                    {BUDGET_RANGES.map((d) => <option key={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Inspiration photo" icon={Camera}>
                  <label
                    htmlFor="inquiry-photo-input"
                    className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-sakura-300 bg-sakura-50 px-4 py-3 cursor-pointer hover:bg-sakura-100 transition"
                    data-testid="inquiry-photo-drop"
                  >
                    <Camera className="h-5 w-5 text-sakura-500" />
                    <span className="text-sm text-sakura-soft truncate">
                      {form.inspiration_photo_name || "Attach an image (optional)"}
                    </span>
                  </label>
                  <input
                    ref={fileRef}
                    id="inquiry-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onFile}
                    data-testid="inquiry-photo-input"
                  />
                  {form.inspiration_photo && (
                    <button type="button" onClick={clearFile} className="mt-2 text-xs font-semibold text-sakura-500 hover:underline" data-testid="inquiry-photo-remove">Remove photo</button>
                  )}
                </Field>
                <Field label="Additional notes" icon={MessageCircle} className="md:col-span-2">
                  <textarea className="input-soft min-h-[120px]" value={form.notes} onChange={set("notes")} placeholder="Anything else I should know — allergies, special requests, timing…" data-testid="inquiry-notes" />
                </Field>
              </div>
              <div className="mt-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-sakura-soft max-w-md">By sending, you agree that I may contact you about this request at the email/phone provided.</p>
                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="inquiry-submit">
                  <Sparkles className="h-4 w-4" /> {submitting ? "Sending…" : "Send Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, icon: Icon, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="flex items-center gap-2 text-sm font-semibold text-sakura-ink mb-1.5">
        {Icon && <Icon className="h-4 w-4 text-sakura-500" />} {label} {required && <span className="text-sakura-500">*</span>}
      </span>
      {children}
    </label>
  );
}
